import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { MultiSelect } from '@/components/ui/multi-select'
import { DeleteButton, EditButton } from '@/components/ui/action-buttons'
import {
  PageContainer,
  PageCard,
  PageHeader,
  PageHeaderContent,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageContent
} from '@/components/ui/page'
import { TablePagination } from '@/components/ui/table-pagination'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Loader2, Plus, Phone, Link2, Building2, User } from 'lucide-react'
import { DataView, DataViewToolbar, type DataViewColumn, type ViewMode } from '@/components/data-view'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useDebounce } from '@/hooks/useDebounce'

// Hooks
import { useTransferDestinations, useTransferDestinationsSearchPage, useTransferDestination } from '@/hooks/useTransferDestinations'
import { useTransferDestinationMutations } from '@/hooks/useTransferDestinationMutations'
import { useAgents } from '@/hooks/useAgents'

// Components
import { AvailabilityWindowsEditor } from '@/components/transfer-destinations/AvailabilityWindowsEditor'
import { AvailabilityWindowsDisplay } from '@/components/transfer-destinations/AvailabilityWindowsDisplay'

// Types
import type { TransferDestinationSummary } from '@/hooks/useTransferDestinations'
import type { TransferDestinationCreateDTO, TransferDestinationUpdateDTO, AvailabilityWindowDTO } from '@/api/generated/models'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

export const Route = createFileRoute('/transfer-destinations')({
  component: TransferDestinations,
  errorComponent: RouteErrorFallback,
})

interface FormData {
  name: string
  display_name: string
  description: string
  destination_type: 'phone' | 'sip_uri'
  destination: string
  scope: 'organization_wide' | 'agent_specific'
  is_active: boolean
  agent_ids: string[]
  availability_windows: AvailabilityWindowDTO[]
}

const initialFormData: FormData = {
  name: '',
  display_name: '',
  description: '',
  destination_type: 'phone',
  destination: '',
  scope: 'organization_wide',
  is_active: true,
  agent_ids: [],
  availability_windows: []
}

function TransferDestinations() {
  // State
  const [view, setView] = useLocalStorage<ViewMode>('audavo:view:transfer-destinations', 'table')
  const [searchTerm, setSearchTerm] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'view' | 'edit'>('view')
  const [selectedDestinationId, setSelectedDestinationId] = useState<string | null>(null)
  const [formData, setFormData] = useState<FormData>(initialFormData)

  const debouncedSearch = useDebounce(searchTerm.trim(), 300)
  const isSearching = debouncedSearch.length > 0

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(() =>
    typeof window !== 'undefined' && window.innerHeight >= 900 ? 50 : 20
  )

  useEffect(() => {
    setPage(0)
  }, [debouncedSearch, pageSize])

  // Queries — list mode fetches a page, search mode hits the backend full-text search.
  const listQuery = useTransferDestinations(pageSize, page * pageSize)
  const searchQuery = useTransferDestinationsSearchPage({
    page,
    pageSize,
    q: debouncedSearch,
    enabled: isSearching,
  })

  const { data, isLoading, error } = isSearching ? searchQuery : listQuery

  const destinations = useMemo((): TransferDestinationSummary[] => {
    return data?.results ?? []
  }, [data])

  const total = data?.total ?? 0

  const { data: agents = [] } = useAgents()
  const { data: selectedDestination } = useTransferDestination(selectedDestinationId)

  // Mutations
  const { createDestination, updateDestination, deleteDestination, updateDestinationAgents } = useTransferDestinationMutations()

  // Update form when full destination is loaded
  useEffect(() => {
    if (selectedDestination) {
      setFormData({
        name: selectedDestination.name,
        display_name: selectedDestination.display_name,
        description: selectedDestination.description || '',
        destination_type: selectedDestination.destination_type as 'phone' | 'sip_uri',
        destination: selectedDestination.destination,
        scope: selectedDestination.scope as 'organization_wide' | 'agent_specific',
        is_active: selectedDestination.is_active,
        agent_ids: selectedDestination.agents?.map(a => a.id) || [],
        availability_windows: selectedDestination.availability_windows ?? []
      })
    }
  }, [selectedDestination])

  // Handlers
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const createData: TransferDestinationCreateDTO = {
      name: formData.name,
      display_name: formData.display_name,
      description: formData.description || null,
      destination_type: formData.destination_type,
      destination: formData.destination,
      scope: formData.scope,
      is_active: formData.is_active,
      agent_ids: formData.scope === 'agent_specific' ? formData.agent_ids : [],
      availability_windows: formData.availability_windows.length > 0 ? formData.availability_windows : null
    }

    try {
      await createDestination.mutateAsync(createData)
      setCreateDialogOpen(false)
      setFormData(initialFormData)
    } catch (error) {
      console.error('Failed to create transfer destination:', error)
    }
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedDestinationId) return

    const updateData: TransferDestinationUpdateDTO = {
      name: formData.name,
      display_name: formData.display_name,
      description: formData.description || null,
      destination_type: formData.destination_type,
      destination: formData.destination,
      scope: formData.scope,
      is_active: formData.is_active,
      availability_windows: formData.availability_windows.length > 0 ? formData.availability_windows : []
    }

    try {
      await updateDestination.mutateAsync({ id: selectedDestinationId, data: updateData })

      // Update agent assignments if scope is agent_specific
      if (formData.scope === 'agent_specific') {
        await updateDestinationAgents.mutateAsync({
          destinationId: selectedDestinationId,
          agentIds: formData.agent_ids
        })
      }

      setDialogMode('view')
      setDetailDialogOpen(false)
    } catch (error) {
      console.error('Failed to update transfer destination:', error)
    }
  }

  const handleDelete = async () => {
    if (!selectedDestinationId) return

    try {
      await deleteDestination.mutateAsync(selectedDestinationId)
      setDetailDialogOpen(false)
      setSelectedDestinationId(null)
    } catch (error) {
      console.error('Failed to delete transfer destination:', error)
    }
  }

  const handleRowClick = (destination: TransferDestinationSummary) => {
    setSelectedDestinationId(destination.id)
    setFormData({
      name: destination.name,
      display_name: destination.display_name,
      description: destination.description || '',
      destination_type: destination.destination_type as 'phone' | 'sip_uri',
      destination: destination.destination,
      scope: destination.scope as 'organization_wide' | 'agent_specific',
      is_active: destination.is_active,
      agent_ids: [],
      availability_windows: destination.availability_windows ?? []
    })
    setDialogMode('view')
    setDetailDialogOpen(true)
  }

  const formatDestination = (type: string, destination: string) => {
    if (type === 'phone') {
      // Format phone number for display
      return destination
    }
    return destination
  }

  const getTypeIcon = (type: string) => {
    return type === 'phone' ? <Phone className="h-4 w-4" /> : <Link2 className="h-4 w-4" />
  }

  const getScopeIcon = (scope: string) => {
    return scope === 'organization_wide' ? <Building2 className="h-4 w-4" /> : <User className="h-4 w-4" />
  }

  const columns: DataViewColumn<TransferDestinationSummary>[] = [
    {
      id: 'name',
      header: 'Name',
      className: 'font-medium',
      cell: (d) => d.display_name || d.name,
    },
    {
      id: 'destination',
      header: 'Ziel',
      cell: (d) => (
        <div className="flex items-center gap-2">
          {getTypeIcon(d.destination_type)}
          <span className="font-mono text-sm">{formatDestination(d.destination_type, d.destination)}</span>
        </div>
      ),
    },
    {
      id: 'scope',
      header: 'Bereich',
      width: '200px',
      cell: (d) => (
        <div className="flex items-center gap-2">
          {getScopeIcon(d.scope)}
          <span>{d.scope === 'organization_wide' ? 'Organisationsweit' : 'Agenten-spezifisch'}</span>
        </div>
      ),
    },
    {
      id: 'agents',
      header: 'Agenten',
      width: '140px',
      cell: (d) =>
        d.scope === 'organization_wide' ? (
          <Badge variant="outline">Alle</Badge>
        ) : (
          <Badge variant="secondary">{d.agent_count || 0} Agenten</Badge>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '120px',
      cell: (d) => (
        <Badge variant={d.is_active ? 'default' : 'secondary'}>
          {d.is_active ? 'Aktiv' : 'Inaktiv'}
        </Badge>
      ),
    },
  ]

  const renderCard = (d: TransferDestinationSummary) => (
    <Card>
      <CardContent className="flex items-center gap-4 p-3">
        <span className="min-w-0 flex-1 truncate font-medium">{d.display_name || d.name}</span>
        <span className="hidden shrink-0 font-mono text-sm text-muted-foreground truncate max-w-[16rem] sm:inline">
          {formatDestination(d.destination_type, d.destination)}
        </span>
        <div className="shrink-0">
          <Badge variant={d.is_active ? 'default' : 'secondary'}>
            {d.is_active ? 'Aktiv' : 'Inaktiv'}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )

  const createButton = (
    <Button onClick={() => setCreateDialogOpen(true)}>
      <Plus className="mr-2 h-4 w-4" />
      Neue Weiterleitung
    </Button>
  )

  const paginationControl = (
    <TablePagination
      currentPage={page + 1}
      totalPages={Math.max(1, Math.ceil(total / pageSize))}
      totalItems={total}
      pageSize={pageSize}
      onPageChange={(p) => setPage(p - 1)}
      onPageSizeChange={setPageSize}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
    />
  )

  const showPagination = !isLoading && !error && (destinations.length > 0 || page > 0)

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <PageHeaderHeading>Weiterleitungen</PageHeaderHeading>
              <PageHeaderDescription>
                Verwalten Sie Telefonnummern und SIP-Endpunkte für Anrufweiterleitungen
              </PageHeaderDescription>
            </PageHeaderTitle>
            <DataViewToolbar
              view={view}
              onViewChange={setView}
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: 'Name, Ziel…',
              }}
              toolbarAction={createButton}
            />
          </PageHeaderContent>
        </PageHeader>

        <PageContent className="p-6">
          <DataView
            items={destinations}
            getRowId={(d) => d.id}
            columns={columns}
            renderCard={renderCard}
            onItemClick={handleRowClick}
            storageKey="audavo:view:transfer-destinations"
            cardGridClassName="grid-cols-1 gap-3 md:grid-cols-1 lg:grid-cols-1"
            view={view}
            onViewChange={setView}
            isLoading={isLoading}
            error={error}
            emptyState={
              isSearching
                ? {
                    icon: Phone,
                    title: 'Keine Treffer',
                    description: `Für „${debouncedSearch}“ wurden keine Weiterleitungen gefunden.`,
                  }
                : {
                    icon: Phone,
                    title: 'Keine Weiterleitungen',
                    description: 'Es wurden noch keine Weiterleitungen erfasst.',
                  }
            }
            pagination={showPagination ? paginationControl : undefined}
          />
        </PageContent>
      </PageCard>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Neue Weiterleitung</DialogTitle>
            <DialogDescription>
              Fügen Sie eine neue Telefonnummer oder SIP-Endpunkt für Anrufweiterleitungen hinzu.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateSubmit}>
            <Tabs defaultValue="settings" className="mt-2">
              <TabsList className="w-full">
                <TabsTrigger value="settings" className="flex-1">Einstellungen</TabsTrigger>
                <TabsTrigger value="schedule" className="flex-1">Zeitplan</TabsTrigger>
              </TabsList>

              <div className="grid mt-4 [&>*]:col-start-1 [&>*]:row-start-1">
                <TabsContent value="settings" forceMount className="data-[state=inactive]:invisible">
                  <div className="grid gap-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="create-name">Interner Name</Label>
                        <Input
                          id="create-name"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="z.B. dr_schmidt_mobile"
                          required
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="create-display-name">Anzeigename</Label>
                        <Input
                          id="create-display-name"
                          value={formData.display_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                          placeholder="Herr Schmidt Mobiltelefon"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="create-description">Grund (Optional)</Label>
                      <Textarea
                        id="create-description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="z.B. Für dringende Fälle"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="create-type">Typ</Label>
                        <Select
                          value={formData.destination_type}
                          onValueChange={(value: 'phone' | 'sip_uri') => setFormData(prev => ({ ...prev, destination_type: value }))}
                        >
                          <SelectTrigger id="create-type" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="phone">Telefonnummer</SelectItem>
                            <SelectItem value="sip_uri">SIP URI</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="create-destination">Ziel</Label>
                        <Input
                          id="create-destination"
                          type={formData.destination_type === 'phone' ? 'tel' : 'text'}
                          value={formData.destination}
                          onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                          placeholder={formData.destination_type === 'phone' ? '+49170123456' : 'sip:user@domain.com'}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="create-scope">Bereich</Label>
                      <Select
                        value={formData.scope}
                        onValueChange={(value: 'organization_wide' | 'agent_specific') => setFormData(prev => ({ ...prev, scope: value }))}
                      >
                        <SelectTrigger id="create-scope" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="organization_wide">Organisationsweit</SelectItem>
                          <SelectItem value="agent_specific">Agenten-spezifisch</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.scope === 'agent_specific' && (
                      <div className="grid gap-2">
                        <Label htmlFor="create-agents">Agenten zuweisen</Label>
                        <MultiSelect
                          items={agents.map(agent => ({
                            id: agent.id,
                            label: agent.name,
                            description: agent.description || undefined
                          }))}
                          selectedItems={formData.agent_ids}
                          onSelectionChange={(selectedIds) => setFormData(prev => ({ ...prev, agent_ids: selectedIds }))}
                          placeholder="Agenten auswählen..."
                          searchPlaceholder="Agenten suchen..."
                        />
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="create-active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                      />
                      <Label htmlFor="create-active">Aktiv</Label>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="schedule" forceMount className="flex flex-col data-[state=inactive]:invisible">
                  <AvailabilityWindowsEditor
                    windows={formData.availability_windows}
                    onChange={(windows) => setFormData(prev => ({ ...prev, availability_windows: windows }))}
                  />
                </TabsContent>
              </div>
            </Tabs>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={createDestination.isPending}>
                {createDestination.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail/Edit Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'view' ? 'Weiterleitungs-Details' : 'Weiterleitung bearbeiten'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'view' ? 'Details der Weiterleitung anzeigen' : 'Weiterleitungs-Einstellungen bearbeiten'}
            </DialogDescription>
          </DialogHeader>

          {dialogMode === 'view' ? (
            <>
              <Tabs defaultValue="settings" className="mt-2">
                <TabsList className="w-full">
                  <TabsTrigger value="settings" className="flex-1">Einstellungen</TabsTrigger>
                  <TabsTrigger value="schedule" className="flex-1">Zeitplan</TabsTrigger>
                </TabsList>

                <div className="grid mt-4 [&>*]:col-start-1 [&>*]:row-start-1">
                  <TabsContent value="settings" forceMount className="data-[state=inactive]:invisible">
                    <div className="grid gap-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Interner Name</Label>
                          <p className="font-medium">{selectedDestination?.name}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Anzeigename</Label>
                          <p className="font-medium">{selectedDestination?.display_name}</p>
                        </div>
                      </div>

                      {selectedDestination?.description && (
                        <div>
                          <Label className="text-muted-foreground">Grund</Label>
                          <p className="font-medium">{selectedDestination.description}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Typ</Label>
                          <div className="flex items-center gap-2 mt-1">
                            {getTypeIcon(selectedDestination?.destination_type || '')}
                            <span className="font-medium">
                              {selectedDestination?.destination_type === 'phone' ? 'Telefonnummer' : 'SIP URI'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Ziel</Label>
                          <p className="font-medium font-mono">
                            {selectedDestination?.destination}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Bereich</Label>
                          <div className="flex items-center gap-2 mt-1">
                            {getScopeIcon(selectedDestination?.scope || '')}
                            <span className="font-medium">
                              {selectedDestination?.scope === 'organization_wide' ? 'Organisationsweit' : 'Agenten-spezifisch'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Status</Label>
                          <Badge
                            variant={selectedDestination?.is_active ? 'default' : 'secondary'}
                            className="mt-1"
                          >
                            {selectedDestination?.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </div>
                      </div>

                      {selectedDestination?.scope === 'agent_specific' && selectedDestination.agents && selectedDestination.agents.length > 0 && (
                        <div>
                          <Label className="text-muted-foreground">Zugewiesene Agenten</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {selectedDestination.agents.map(agent => (
                              <Badge key={agent.id} variant="outline">
                                {agent.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="schedule" forceMount className="flex flex-col data-[state=inactive]:invisible">
                    <AvailabilityWindowsDisplay windows={selectedDestination?.availability_windows} />
                  </TabsContent>
                </div>
              </Tabs>

              <DialogFooter className="mt-4">
                <EditButton
                  onClick={() => setDialogMode('edit')}
                />
                <DeleteButton
                  onDelete={handleDelete}
                  itemName={selectedDestination?.display_name || ''}
                  confirmationTitle="Weiterleitung löschen?"
                  confirmationDescription="Sind Sie sicher, dass Sie diese Weiterleitung löschen möchten?"
                />
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleEditSubmit}>
              <Tabs defaultValue="settings" className="mt-2">
                <TabsList className="w-full">
                  <TabsTrigger value="settings" className="flex-1">Einstellungen</TabsTrigger>
                  <TabsTrigger value="schedule" className="flex-1">Zeitplan</TabsTrigger>
                </TabsList>

                <div className="grid mt-4 [&>*]:col-start-1 [&>*]:row-start-1">
                  <TabsContent value="settings" forceMount className="data-[state=inactive]:invisible">
                    <div className="grid gap-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                          <Label htmlFor="edit-name">Interner Name</Label>
                          <Input
                            id="edit-name"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="edit-display-name">Anzeigename</Label>
                          <Input
                            id="edit-display-name"
                            value={formData.display_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="edit-description">Grund (Optional)</Label>
                        <Textarea
                          id="edit-description"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                          <Label htmlFor="edit-type">Typ</Label>
                          <Select
                            value={formData.destination_type}
                            onValueChange={(value: 'phone' | 'sip_uri') => setFormData(prev => ({ ...prev, destination_type: value }))}
                          >
                            <SelectTrigger id="edit-type" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="phone">Telefonnummer</SelectItem>
                              <SelectItem value="sip_uri">SIP URI</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="edit-destination">Ziel</Label>
                          <Input
                            id="edit-destination"
                            type={formData.destination_type === 'phone' ? 'tel' : 'text'}
                            value={formData.destination}
                            onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="edit-scope">Bereich</Label>
                        <Select
                          value={formData.scope}
                          onValueChange={(value: 'organization_wide' | 'agent_specific') => setFormData(prev => ({ ...prev, scope: value }))}
                        >
                          <SelectTrigger id="edit-scope" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="organization_wide">Organisationsweit</SelectItem>
                            <SelectItem value="agent_specific">Agenten-spezifisch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {formData.scope === 'agent_specific' && (
                        <div className="grid gap-2">
                          <Label htmlFor="edit-agents">Agenten zuweisen</Label>
                          <MultiSelect
                            items={agents.map(agent => ({
                              id: agent.id,
                              label: agent.name,
                              description: agent.description || undefined
                            }))}
                            selectedItems={formData.agent_ids}
                            onSelectionChange={(selectedIds) => setFormData(prev => ({ ...prev, agent_ids: selectedIds }))}
                            placeholder="Agenten auswählen..."
                            searchPlaceholder="Agenten suchen..."
                          />
                        </div>
                      )}

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="edit-active"
                          checked={formData.is_active}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                        />
                        <Label htmlFor="edit-active">Aktiv</Label>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="schedule" forceMount className="flex flex-col data-[state=inactive]:invisible">
                    <AvailabilityWindowsEditor
                      windows={formData.availability_windows}
                      onChange={(windows) => setFormData(prev => ({ ...prev, availability_windows: windows }))}
                    />
                  </TabsContent>
                </div>
              </Tabs>

              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setDialogMode('view')}>
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  disabled={updateDestination.isPending || updateDestinationAgents.isPending}
                >
                  {(updateDestination.isPending || updateDestinationAgents.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Speichern
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}